import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { api, setUnauthorizedHandler } from '../lib/api'
import { getCsrfToken, clearCsrfToken } from '../lib/csrf'
import { SessionExpired } from '../components/modals'
import type { AuthStatus, User } from './types'
import { resetAutopilotAccess } from '../lib/autopilotAccess'

interface AuthValue {
  user: User | null
  status: AuthStatus
  login: (email: string, password: string, remember?: boolean) => Promise<void>
  register: (email: string, password: string, acceptTerms: boolean, displayName?: string, locale?: string) => Promise<void>
  logout: () => Promise<void>
  /** Re-fetch /auth/me so the token meter reflects a spend (best-effort). */
  refreshUser: () => Promise<void>
}

const AuthCtx = createContext<AuthValue | null>(null)

/** The context if mounted, else null — for hooks that must not throw outside the
 *  provider (route tests render screens bare; a lock that throws there would make
 *  every screen test carry an auth mock for a button state). */
export function useAuthOptional(): AuthValue | null {
  return useContext(AuthCtx)
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [sessionExpired, setSessionExpired] = useState(false)
  const navigate = useNavigate()
  const booted = useRef(false)
  // "Session expired" only makes sense if there WAS a session. The boot /auth/me
  // probe 401s for every anonymous visitor (incl. on public pages) — without this
  // guard that 401 would pop the "Sesja wygasła" modal to people who never logged
  // in. Only a 401 AFTER a real authentication is a genuine expiry.
  const wasAuthed = useRef(false)

  const handleUnauthorized = useCallback(() => {
    setUser(null)
    if (wasAuthed.current) setSessionExpired(true)
    wasAuthed.current = false
  }, [])

  useEffect(() => {
    if (booted.current) return // StrictMode double-mount guard
    booted.current = true
    setUnauthorizedHandler(handleUnauthorized)
    api<User>('/auth/me')
      .then((u) => { wasAuthed.current = true; setUser(u) })
      .catch(() => setUser(null))
      .finally(() => setStatus('ready'))
  }, [navigate, handleUnauthorized])

  // All four are useCallback-stable (they close over only setUser + module fns) so
  // the context value below is referentially stable across renders. This matters:
  // an unstable `refreshUser` used as a useEffect dep (e.g. Billing's mount meter
  // refresh) would loop — refreshUser → setUser(new obj) → re-render → new fn → effect
  // re-fires → unbounded GET /auth/me.
  const login = useCallback(async (email: string, password: string, remember = false) => {
    const u = await api<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, remember }),
    })
    clearCsrfToken()         // session id rotated on login → old token is stale
    await getCsrfToken()     // pre-warm a fresh token bound to the new session
    wasAuthed.current = true // a real session now exists → a later 401 = true expiry
    setUser(u)
  }, [])

  const register = useCallback(
    async (email: string, password: string, acceptTerms: boolean, displayName?: string, locale?: string) => {
      // The sign-up form has always ASKED for a name; until now it was collected
      // and thrown away here, so every account started nameless and the dashboard
      // fell back to greeting people by their email local part.
      //
      // `locale` is the language the sign-up screen is in. Without it `users.locale`
      // fell back to the column default `pl` and a visitor from /en/* got Polish
      // e-mails and a Polish UI after login (PROD1 FR-3). The caller passes it because
      // this provider sits OUTSIDE I18nProvider (main.tsx).
      await api('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          accept_terms: acceptTerms,
          display_name: (displayName ?? '').trim() || undefined,
          locale,
        }),
      })
    },
    [],
  )

  const logout = useCallback(async () => {
    // Best-effort: clear local auth state even if the network call fails (the
    // server may have already dropped the session). Swallow the error so a
    // flaky response can't strand the UI logged-in or surface as an unhandled
    // rejection in callers that fire-and-forget (e.g. the sidebar chip).
    try {
      await api('/auth/logout', { method: 'POST' })
    } catch {
      /* ignore — logout is best-effort */
    } finally {
      clearCsrfToken()
      resetAutopilotAccess() // the next account in this tab must not inherit this one's publishing answer
      wasAuthed.current = false // deliberate logout is not an "expiry"
      setUser(null)
    }
  }, [])

  const refreshUser = useCallback(async () => {
    // Best-effort: a failed refresh must not log the user out or throw into a
    // fire-and-forget caller. The centralized 401 path in api() already handles a
    // real session expiry; here we just swallow transient errors.
    try {
      const u = await api<User>('/auth/me')
      wasAuthed.current = true
      setUser(u)
    } catch {
      /* ignore — meter stays at its last known value */
    }
  }, [])

  const value = useMemo(
    () => ({ user, status, login, register, logout, refreshUser }),
    [user, status, login, register, logout, refreshUser],
  )

  const goLogin = useCallback(() => {
    setSessionExpired(false)
    navigate('/login')
  }, [navigate])

  return (
    <AuthCtx.Provider value={value}>
      {children}
      {sessionExpired && <SessionExpired onLogin={goLogin} />}
    </AuthCtx.Provider>
  )
}
